<template>
  <div class="gallery-wrapper">
    <div
      ref="gallery"
      class="gallery"
      :class="{ 'gallery--low-power': lowPower }"
    >
      <div
        v-for="(image, index) in images"
        :key="image.id"
        ref="galleryItems"
        class="gallery__item"
        @click="handleImageClick(image)"
      >
        <Video
          v-if="image.mediaContentType === 'VIDEO'"
          :video="image"
          :intersected="intersectStatus[index]"
          @lowPower="lowPower = true"
        />
        <Picture v-else :images="image" :intersected="intersectStatus[index]" />
      </div>
    </div>
    <ToastMessage class="toast"> Tap to zoom </ToastMessage>
  </div>
  <div class="dots">
    <button
      v-for="(item, index) in images"
      :key="item.id"
      class="dot"
      :class="{ 'dot--active': intersectStatus[index] }"
      @click="goToDot(index)"
    >
      <span class="dot__dot"></span>
    </button>
  </div>
  <ImageViewer
    v-if="isImageViewerOpen"
    :image="imageViewerImage"
    @close="isImageViewerOpen = false"
  />
</template>

<script>
import { mapState } from "vuex";
import { unwrapNodes } from "@/helpers/graphql";
import Picture from "@/components/Picture.vue";
import Video from "@/components/Video.vue";
import ImageViewer from "@/components/product-display/ImageViewer.vue";
import ToastMessage from "@/components/ToastMessage.vue";

export default {
  name: "ProductGallery",
  components: {
    Picture,
    Video,
    ImageViewer,
    ToastMessage,
  },
  inject: ["mq"],
  data() {
    return {
      observers: [],
      intersectStatus: [],
      isImageViewerOpen: false,
      imageViewerImage: null,
      lowPower: false,
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    images() {
      const medias = this.product.media
        ? unwrapNodes(this.product.media.edges)
        : [];
      const video = medias.find((media) => media.mediaContentType === "VIDEO");

      const allImages = this.product.images
        ? unwrapNodes(this.product.images?.edges)
        : [];
      const imagesWithoutFacebook = allImages.filter(
        (image) => image.altText !== "fb-feed"
      );

      if (video) {
        imagesWithoutFacebook.splice(1, 0, video);
      }

      return imagesWithoutFacebook;
    },
  },
  mounted() {
    this.intersectStatus = Array(this.images.length).fill(false);

    // setup the observers for each gallery item
    this.$refs.galleryItems.forEach((item, index) => {
      this.setupObserver(item, index);
    });

    // Make sure first index is the first slide
    this.goToDot(0);
  },
  beforeUnmount() {
    this.observers.forEach((observer) => observer.disconnect());
  },
  methods: {
    handleImageClick(image) {
      if (image.mediaContentType === "VIDEO") {
        return;
      }

      this.imageViewerImage = {
        blurry: image.blurry,
        url: this.mq.mdPlus
          ? image.imageViewerDesktop
          : image.imageViewerMobile,
      };
      this.isImageViewerOpen = true;
    },
    goToDot(index) {
      const galleryChildren = this.$refs.gallery.children;

      galleryChildren[index].scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    },
    setupObserver(element, index) {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      };

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.intersectStatus[index] = true;
        } else {
          this.intersectStatus[index] = false;
        }
      }, options);

      observer.observe(element);
      this.observers.push(observer);
    },
  },
};
</script>

<style lang="scss" scoped>
.gallery-wrapper {
  position: relative;

  .toast {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
  }
}

.gallery {
  display: flex;
  overflow: auto;
  width: 100%;
  flex-flow: row nowrap;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    display: none;
  }

  @include respond-above(md) {
    overflow: visible;
    white-space: normal;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2px;
  }

  & > ::v-deep(*) {
    scroll-snap-align: center;
    scroll-snap-stop: always;
    flex: none;
    width: 100%;
    overflow: hidden;

    @include respond-above(md) {
      display: block;
      width: 100%;
      margin-right: 0;

      &:nth-child(3n + 1) {
        grid-column: 1 / span 2;
      }
    }
  }
}

.gallery:not(.gallery--low-power) {
  .gallery__item {
    ::v-deep() {
      // Fixes bug with iOS Safari 17 video touch events issue on mobile
      .video__wrapper {
        position: relative;
      }

      .video__wrapper::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
      }
    }
  }
}

.dots {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;

  @include respond-above(md) {
    display: none;
  }

  & > .dot {
    outline: none;
    border: none;
    background: none;
    padding: 5px;

    &.dot--active {
      .dot__dot {
        width: 4px;
        height: 4px;
      }
    }

    .dot__dot {
      display: block;
      background: $color-dark;
      width: 2px;
      height: 2px;
      border-radius: 50%;
    }
  }
}
</style>
